/**
 * What each outreach tool is *for*, and therefore which number says it worked.
 *
 * Every tool is measured by the same funnel, because the same things can be
 * observed about all of them: did we reach her, did she answer, did she turn up,
 * did she stay. What the data can never say is which of those *is* the goal —
 * that messaging a cancelling member is about the reply, while inviting her to a
 * welcome event is about whether she came. Only someone who knows why the tool
 * exists can say that, so it's written down here rather than inferred.
 *
 * This is the file to edit when a tool's purpose changes. The panel, the
 * headline verdict and the wording all read from it, so they can't disagree.
 */

export type OutreachSource =
  | "CHAT_AUTOMATIONS"
  | "ONLINE_NOW"
  | "RETENTION"
  | "CANCELLATIONS"
  | "NEW_MEMBERS"
  | "INVITE_CAMPAIGNS"
  | "INVITE_COHORT"
  | "MEMBER_EVENTS"
  | "FIND_EVENTS"
  | "SHARE_EVENT"
  | "ZONE_OUTREACH";

/** The funnel fields a panel can put on show. */
export type FunnelKey =
  | "reached"
  | "responded"
  | "accepted"
  | "declined"
  | "attendedInvited"
  | "attendedAny"
  | "stillZero"
  | "loggedIn"
  | "stayed"
  | "leaving"
  | "cancelled";

export type BreakdownKey = "CAMPAIGN" | "PRIORITY" | "CHALLENGE" | "FILTER" | "RISK_THEN";

export interface FunnelStep {
  key: FunnelKey;
  label: string;
  /** What this number counts, in the words of the tool it belongs to. */
  hint: string;
}

export interface SourceMeta {
  label: string;
  emoji: string;
  /** What the tool is trying to achieve, in one sentence. */
  goal: string;
  /**
   * The step whose share of `reached` is the verdict. Undefined for the tools
   * with no single goal — the board says so rather than inventing one.
   */
  headline?: FunnelKey;
  /** The funnel steps worth showing, in the order they happen. */
  steps: FunnelStep[];
  /** How the members are sliced underneath, and what to call that slice. */
  breakdowns: { key: BreakdownKey; label: string }[];
  /** Shown instead of a verdict when the tool has no single goal. */
  note?: string;
}

const RESPONDED: FunnelStep = {
  key: "responded",
  label: "Replied",
  hint: "She wrote back in chat after we reached her.",
};
const ATTENDED_ANY: FunnelStep = {
  key: "attendedAny",
  label: "Came to a conversation",
  hint: "She attended any conversation after we reached her — not necessarily the one we mentioned.",
};
const STAYED: FunnelStep = {
  key: "stayed",
  label: "Still a member",
  hint: "Subscribed and renewing right now.",
};
const CANCELLED: FunnelStep = {
  key: "cancelled",
  label: "Left",
  hint: "Her subscription has ended.",
};
const BY_CAMPAIGN = { key: "CAMPAIGN" as const, label: "By campaign" };
const BY_RISK = { key: "RISK_THEN" as const, label: "By risk when we reached her" };

export const SOURCES: Record<OutreachSource, SourceMeta> = {
  ONLINE_NOW: {
    label: "Online Now",
    emoji: "🟢",
    goal: "Catch members who are in the app but haven't been to a conversation for a while.",
    headline: "attendedAny",
    steps: [
      { key: "reached", label: "Reached", hint: "Members messaged from Online Now." },
      RESPONDED,
      ATTENDED_ANY,
      STAYED,
      CANCELLED,
    ],
    breakdowns: [BY_CAMPAIGN, BY_RISK],
  },

  RETENTION: {
    label: "Retention",
    emoji: "💗",
    goal: "Pull drifting members back in with an event invitation, then a personal message.",
    headline: "attendedAny",
    steps: [
      {
        key: "reached",
        label: "Reached",
        hint: "Members sent an invitation, a share or a Cycle 2 message.",
      },
      RESPONDED,
      ATTENDED_ANY,
      {
        key: "loggedIn",
        label: "Interested",
        hint: "She logged in after we reached her but hasn't come to anything yet.",
      },
      STAYED,
      CANCELLED,
    ],
    breakdowns: [{ key: "CAMPAIGN", label: "By cycle" }, BY_RISK],
  },

  CANCELLATIONS: {
    label: "Cancellations",
    emoji: "🕊️",
    goal: "Offer a 1:1 to members on their way out, and keep the ones we can.",
    headline: "stayed",
    steps: [
      {
        key: "reached",
        label: "Reached",
        hint: "Members offered a 1:1 before their membership ended.",
      },
      RESPONDED,
      {
        key: "stayed",
        label: "Stayed",
        hint: "Her end date has passed and she's still subscribed — she turned it around.",
      },
      {
        key: "leaving",
        label: "Too early to say",
        hint: "Still subscribed, but her end date hasn't arrived yet. She counts as neither saved nor lost.",
      },
      {
        key: "cancelled",
        label: "Still left",
        hint: "Her end date passed and her membership ended.",
      },
    ],
    breakdowns: [{ key: "CAMPAIGN", label: "Scheduled vs trial" }],
  },

  NEW_MEMBERS: {
    label: "New Members",
    emoji: "🌱",
    goal: "Reach new members early, get them to a first conversation and build the habit.",
    headline: "attendedAny",
    steps: [
      { key: "reached", label: "Reached", hint: "New members sent a personal check-in." },
      RESPONDED,
      ATTENDED_ANY,
      {
        key: "stillZero",
        label: "Still on zero",
        hint: "Reached, and still hasn't been to a single conversation.",
      },
      STAYED,
      CANCELLED,
    ],
    breakdowns: [
      { key: "PRIORITY", label: "By why she wants English" },
      { key: "CHALLENGE", label: "By what makes practising hard" },
      BY_CAMPAIGN,
    ],
  },

  INVITE_CAMPAIGNS: {
    label: "Invite Campaigns",
    emoji: "🎟️",
    goal: "Get members to the event their campaign exists for — a welcome, a CXT, a Big Sister or a coach event.",
    headline: "attendedAny",
    steps: [
      { key: "reached", label: "Invited", hint: "Members sent an invitation by a campaign." },
      {
        key: "accepted",
        label: "Accepted",
        hint: "She said yes to the invitation (self-signups don't count).",
      },
      {
        key: "attendedInvited",
        label: "Came to that event",
        hint: "She turned up to the exact event we invited her to.",
      },
      {
        key: "attendedAny",
        label: "Came to any conversation",
        hint: "She turned up to something after the invitation — missing the event we picked but joining another still counts as the campaign working.",
      },
    ],
    breakdowns: [{ key: "CAMPAIGN", label: "By campaign" }],
  },

  MEMBER_EVENTS: {
    label: "Member Events",
    emoji: "👤",
    goal: "Put the right event in front of one member, from her own page.",
    headline: "attendedInvited",
    steps: [
      {
        key: "reached",
        label: "Invited or shared with",
        hint: "Members sent an event from their page.",
      },
      { key: "accepted", label: "Accepted", hint: "She said yes to the invitation." },
      { key: "declined", label: "Declined", hint: "She said no." },
      { key: "attendedInvited", label: "Came to it", hint: "She turned up to that event." },
      ATTENDED_ANY,
    ],
    breakdowns: [BY_RISK],
  },

  FIND_EVENTS: {
    label: "Find Events",
    emoji: "🔍",
    goal: "Find a member an event that suits her, and get her there.",
    headline: "attendedInvited",
    steps: [
      {
        key: "reached",
        label: "Invited or shared with",
        hint: "Members sent an event found here.",
      },
      { key: "accepted", label: "Accepted", hint: "She said yes to the invitation." },
      { key: "declined", label: "Declined", hint: "She said no." },
      { key: "attendedInvited", label: "Came to it", hint: "She turned up to that event." },
      ATTENDED_ANY,
    ],
    breakdowns: [BY_RISK],
  },

  INVITE_COHORT: {
    label: "Invite a Cohort",
    emoji: "🎯",
    goal: "Whatever this week needs — a hand-built group invited to one event.",
    note: "This tool is used for a different reason every time, so there's no single number that says it worked. What's worth knowing is which cohorts we keep building.",
    steps: [
      { key: "reached", label: "Invited", hint: "Members invited as part of a cohort." },
      { key: "accepted", label: "Accepted", hint: "She said yes to the invitation." },
      { key: "attendedInvited", label: "Came to it", hint: "She turned up to that event." },
      ATTENDED_ANY,
    ],
    breakdowns: [{ key: "FILTER", label: "By filter used" }, BY_RISK],
  },

  CHAT_AUTOMATIONS: {
    label: "Chat Automations",
    emoji: "💬",
    goal: "Send a message to a list of members — for whatever reason that send has.",
    note: "The goal is different every send, so the only fair questions are how many we reached and how many wrote back.",
    headline: "responded",
    steps: [{ key: "reached", label: "Reached", hint: "Members sent a bulk message." }, RESPONDED],
    breakdowns: [BY_CAMPAIGN, BY_RISK],
  },

  SHARE_EVENT: {
    label: "Share Event",
    emoji: "🔗",
    goal: "Give members a heads-up about an event.",
    note: "Shown for the volume only — a share is a nudge, not a campaign with an outcome to hold it to.",
    steps: [
      { key: "reached", label: "Shared with", hint: "Members sent an event heads-up." },
      { key: "attendedInvited", label: "Came to it", hint: "She turned up to that event." },
    ],
    breakdowns: [BY_RISK],
  },

  ZONE_OUTREACH: {
    label: "Zone outreach",
    emoji: "🌍",
    goal: "Fill a zone's event from the calendar or the zone insights.",
    headline: "attendedInvited",
    steps: [
      { key: "reached", label: "Invited or shared with", hint: "Members reached by a zone send." },
      { key: "accepted", label: "Accepted", hint: "She said yes to the invitation." },
      { key: "attendedInvited", label: "Came to it", hint: "She turned up to that event." },
    ],
    breakdowns: [{ key: "FILTER", label: "By zone" }, BY_RISK],
  },
};

/**
 * The order the board reads in — the tools with a goal to meet first, the
 * volume-only ones last. Not alphabetical and not by size: a Community Manager
 * opens this to ask "are we saving people", and the tools that answer that
 * should be the ones she lands on.
 */
export const SOURCE_ORDER: OutreachSource[] = [
  "CANCELLATIONS",
  "RETENTION",
  "ONLINE_NOW",
  "NEW_MEMBERS",
  "INVITE_CAMPAIGNS",
  "MEMBER_EVENTS",
  "FIND_EVENTS",
  "INVITE_COHORT",
  "ZONE_OUTREACH",
  "CHAT_AUTOMATIONS",
  "SHARE_EVENT",
];

/**
 * The fallback for outreach the screen can't name.
 *
 * Two things land here: sends from before the log recorded which screen they
 * came from, and a tool the API knows about that this file hasn't been taught
 * yet. Neither is shown on the board — those rows are filtered out, because a
 * row nobody can act on is just something to scroll past — but the fallback has
 * to exist so an unknown source renders as words rather than crashing.
 */
export const UNKNOWN_TOOL: SourceMeta = {
  label: "Other outreach",
  emoji: "•",
  goal: "Outreach this screen can't attribute to a tool.",
  steps: [{ key: "reached", label: "Reached", hint: "Members reached." }, RESPONDED, ATTENDED_ANY],
  breakdowns: [BY_CAMPAIGN],
};

export const metaFor = (source?: string | null): SourceMeta =>
  (source && SOURCES[source as OutreachSource]) || UNKNOWN_TOOL;

/**
 * What every number on this screen means, in one place.
 *
 * The funnel steps each carry their own hint, but those are phrased for the tool
 * they belong to — "members offered a 1:1 before their membership ended". These
 * are the general definitions behind them, for someone reading the board who
 * wants to know what "reached" counts before trusting a percentage. Kept here so
 * the help dialog and the column tooltips can't drift apart.
 */
export interface MetricDefinition {
  term: string;
  detail: string;
}

/**
 * Which definition explains each funnel measure.
 *
 * Keyed off the measure, not the label, because the label changes with the tool
 * — "Reached" on Online Now is "Invited" on a campaign and "Shared with" on
 * Share Event, and all three are the same count of distinct members. Looking up
 * by wording would leave most of them without an answer.
 */
export const TERM_FOR_KEY: Record<FunnelKey, string> = {
  reached: "Reached",
  responded: "Replied",
  accepted: "Accepted",
  declined: "Declined",
  attendedInvited: "Came to that event",
  attendedAny: "Came to a conversation",
  stillZero: "Still on zero",
  loggedIn: "Interested",
  stayed: "Still a member",
  leaving: "Too early to say",
  cancelled: "Left",
};

export const METRIC_DEFINITIONS: MetricDefinition[] = [
  {
    term: "Reached",
    detail:
      "How many different members this tool contacted in the period. People, not messages — a member invited to three events and messaged twice is one member reached, not five.",
  },
  {
    term: "Sends",
    detail:
      "How many individual messages, invitations or shares it took to reach them. Always the bigger number, and shown next to Reached so the two are never confused.",
  },
  {
    term: "Replied",
    detail:
      "She wrote back in chat after we contacted her. Read from the captured 1:1 archive, so it only counts chat — a member who replied by email, or who simply turned up without saying anything, isn't in here. It undercounts rather than overcounts.",
  },
  {
    term: "Accepted",
    detail:
      "She said yes to an invitation we sent. Members who found the event themselves and signed up don't count, however keen they were — that isn't the tool's doing.",
  },
  {
    term: "Declined",
    detail: "She said no to an invitation we sent.",
  },
  {
    term: "Came to that event",
    detail:
      "She turned up to the exact event we put in front of her. The strictest test on the board.",
  },
  {
    term: "Came to a conversation",
    detail:
      "She turned up to any conversation at all after we reached her — not necessarily the one we mentioned. Missing the event we picked but joining another one still counts, because the point was getting her talking.",
  },
  {
    term: "Still on zero",
    detail:
      "We reached her and she still hasn't been to a single conversation since. The number to watch on New Members.",
  },
  {
    term: "Interested",
    detail:
      "She logged in after we reached her but hasn't come to anything yet. A real signal, but a weaker one than turning up — she can open the app and drift away again.",
  },
  {
    term: "Still a member",
    detail: "Her subscription is live and renewing right now.",
  },
  {
    term: "Too early to say",
    detail:
      "She's still subscribed, but it's set to end on a date that hasn't arrived. She counts as neither saved nor lost — calling her cancelled the day we message her would make every save look like a failure, and calling her saved would make every one look like a win.",
  },
  {
    term: "Left",
    detail: "Her membership has ended.",
  },
  {
    term: "Median conversations",
    detail:
      "Half the members reached have been to this many conversations or more since. The median rather than the average, so one very keen member can't flatter a group who mostly came to nothing.",
  },
  {
    term: "▲ ▼ points",
    detail:
      "How this changed against the equally long period immediately before — last 14 days against the 14 before, June against May. It compares the percentage, not the count, because reaching twice as many people doubles every count without anything having improved. Green is the better direction for that row, so a fall in members who left is green.",
  },
  {
    term: "Did it meet its goal?",
    detail:
      "Each tool is judged by the one number that is its purpose — Cancellations by who stayed, Invite Campaigns by who came, Chat Automations by who replied. A few tools have no single goal because they're used for something different every time; those say so instead of inventing one.",
  },
  {
    term: "Measured from when?",
    detail:
      "Everything is measured from the first time that particular tool reached her in the period. A member reached by Retention in June and Online Now in July is counted under both, each against what she did next — so no tool gets credit for another's work.",
  },
];

/** Raw enum keys (NEW_NEVER_ATTENDED, retention:dormant_cycle2) into words. */
export const humanize = (raw?: string | null): string => {
  if (!raw) {
    return "—";
  }
  return raw
    .replace(/^[a-z_]+:/, "")
    .split(/[_\s]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};
